import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ConnectorWithOAuth } from "../../shared/connectors/getTargetValue";
import { connectorsByIdentifier } from "../../shared/connectors";
import { WORKFLOW_CODE_SEARCH_FLAG } from "../../shared/featureFlagNames";
import { queries } from "../../shared/queries";
import type { SpaceMode } from "../../shared/schema";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { useQuery } from "../hooks/useQuery";
import { useSpaceDisplayName } from "../hooks/useSpaceDisplayName";
import { useSpaces } from "../hooks/useSpaces";
import { parseCommitData } from "../lib/parseCommitData";
import { rankedSearchMatches } from "../lib/searchRanking";
import { runStepOptimistically } from "../lib/runStepOptimistically";
import { playClick } from "../lib/sounds";
import { spaceIcon } from "../lib/spaceIcon";
import { navigate, routes, useRouteMatch } from "../lib/urls";
import { CircleMesh } from "./CircleMesh";
import { parseBadgeConfig } from "./CircleMeshEditor";
import { ConnectorLogo } from "./ConnectorLogo";
import { EmptyState } from "./EmptyState";
import { Icon, type IconName } from "./Icon";
import { Keycap } from "./Keycap";
import { Modal } from "./Modal";
import { SearchContainer, SearchInput } from "./SearchField";
import { Text } from "./Text";

const Container = styled("div")`
  width: 424px;
  height: 440px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Results = styled("div")`
  flex: 1;
  overflow-y: auto;
  padding: 0 4px;
`;

const SectionHeader = styled("div")`
  display: flex;
  padding: 10px 8px 6px 8px;
  color: var(--text-secondary);
`;

const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  cursor: pointer;
  border-radius: 8px;
  &[data-highlighted="true"] {
    background: var(--alpha4);
  }
  & > .select-hint {
    display: none;
    margin-left: auto;
    flex-shrink: 0;
  }
  &[data-highlighted="true"] > .select-hint {
    display: flex;
  }
`;

const Footer = styled("div")`
  display: flex;
  height: 40px;
  padding: 0 12px;
  border-top: 0.5px solid var(--border-alpha);
  justify-content: space-between;
  align-items: center;
`;

const Instructions = styled("div")`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  gap: 6px;
`;

type QuickLink = {
  type: "quickLink";
  id: string;
  icon: IconName;
  label: string;
  route:
    | typeof routes.recents
    | typeof routes.branches
    | typeof routes.favorites
    | typeof routes.connectors
    | typeof routes.chats
    | typeof routes.personal
    | typeof routes.skills
    | typeof routes.monitoring;
};

type SpaceItem = {
  type: "space";
  id: string;
  name: string;
  mode: SpaceMode;
};

type ChatItem = {
  type: "chat";
  id: string;
  name: string;
};

type WorkflowItem = {
  type: "workflow";
  id: string;
  name: string;
  spaceId: string;
  spaceName: string;
  badgeSeed: string | null;
  pausedAt: number | null;
};

type ConnectorItem = { type: "connector" } & ConnectorWithOAuth;

type ActionItem = {
  type: "action";
  id: string;
  icon: IconName;
  label: string;
  action: () => void;
};

type SearchItem =
  | ActionItem
  | QuickLink
  | SpaceItem
  | ChatItem
  | WorkflowItem
  | ConnectorItem;

const quickLinks: QuickLink[] = [
  {
    type: "quickLink",
    id: "recents",
    icon: "history",
    label: "Recents",
    route: routes.recents,
  },
  {
    type: "quickLink",
    id: "branches",
    icon: "git-branch",
    label: "Branches",
    route: routes.branches,
  },
  {
    type: "quickLink",
    id: "favorites",
    icon: "heart",
    label: "Favorites",
    route: routes.favorites,
  },
  {
    type: "quickLink",
    id: "chat",
    icon: "ask",
    label: "Chat",
    route: routes.chats,
  },
  {
    type: "quickLink",
    id: "personal",
    icon: "circle-user-round",
    label: "Personal workflows",
    route: routes.personal,
  },
  {
    type: "quickLink",
    id: "apps",
    icon: "api-key-custom",
    label: "Connectors",
    route: routes.connectors,
  },
  {
    type: "quickLink",
    id: "skills",
    icon: "graduation-cap",
    label: "Skills",
    route: routes.skills,
  },
  {
    type: "quickLink",
    id: "monitoring",
    icon: "scan-heart",
    label: "Monitoring",
    route: routes.monitoring,
  },
];

const animalIcons: IconName[] = ["rabbit", "squirrel", "snail", "bird", "rat"];

type Props = {
  open: boolean;
  onClose: () => void;
};

export function CommandBar({ open, onClose }: Props) {
  const z = useZero();
  const [query, setQuery] = useState("");
  const workflowCodeSearchEnabled =
    useFeatureFlag(WORKFLOW_CODE_SEARCH_FLAG) === true;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [animalIcon] = useState(
    () =>
      animalIcons[Math.floor(Math.random() * animalIcons.length)] ?? "rabbit"
  );

  const routeMatch = useRouteMatch(routes.space.workflow);
  const onWorkflowPage = routeMatch[0] as boolean;
  const routeParams = onWorkflowPage
    ? (routeMatch[1] as Record<string, string>)
    : {};
  const workflowId = routeParams.workflowId ?? "";
  const urlBranchId = routeParams.branchId ?? "";
  const [activeBranch] = useQuery(
    queries.loadBranch({
      workflowId: onWorkflowPage ? workflowId : "",
      branchId: urlBranchId || undefined,
    })
  );
  const headCommitFiles = useMemo(
    () => activeBranch?.headCommit?.files ?? [],
    [activeBranch?.headCommit?.files]
  );

  const [recentPresences] = useQuery(
    queries.userRecentWorkflows({
      activitySince: Number.MAX_SAFE_INTEGER,
    })
  );
  const [allWorkflows] = useQuery(queries.allWorkflows());
  const [workflowsByCodeSearch] = useQuery(
    queries.workflowsByCodeSearch({
      searchText: workflowCodeSearchEnabled ? query : "",
    })
  );
  const [readableSpaces] = useSpaces({ settingsView: true });
  const spaces = useMemo(
    () => readableSpaces.filter((s) => s.mode !== "personal"),
    [readableSpaces]
  );
  const [chats] = useQuery(queries.personalChatTabs());
  const [connectors] = useQuery(queries.connectedAppsIndex());
  const displayName = useSpaceDisplayName();

  const spaceNameMap = useMemo(
    () => new Map(spaces.map((s) => [s.id, displayName(s)])),
    [spaces, displayName]
  );

  const spaceItems: SpaceItem[] = useMemo(
    () =>
      spaces.map((s) => ({
        type: "space" as const,
        id: s.id,
        name: displayName(s),
        mode: s.mode,
      })),
    [spaces, displayName]
  );

  const recentWorkflows: WorkflowItem[] = useMemo(
    () =>
      recentPresences
        .filter((p) => p.workflow)
        .map((p) => ({
          type: "workflow" as const,
          id: p.workflow!.id,
          name: p.workflow!.name,
          spaceId: p.workflow!.space!.id,
          spaceName: displayName(p.workflow!.space!),
          badgeSeed: p.workflow!.badgeSeed,
          pausedAt: p.workflow!.pausedAt,
        })),
    [recentPresences, displayName]
  );

  const searchableWorkflows: WorkflowItem[] = useMemo(
    () =>
      allWorkflows.map((w) => ({
        type: "workflow" as const,
        id: w.id,
        name: w.name,
        spaceId: w.spaceId,
        spaceName: spaceNameMap.get(w.spaceId) ?? "",
        badgeSeed: w.badgeSeed,
        pausedAt: w.pausedAt,
      })),
    [allWorkflows, spaceNameMap]
  );

  const appItems: ConnectorItem[] = useMemo(
    () =>
      connectors.map((a) => ({
        ...a,
        type: "connector" as const,
      })),
    [connectors]
  );

  const chatItems: ChatItem[] = useMemo(
    () =>
      [...chats]
        .sort((a, b) => b.lastActiveAt - a.lastActiveAt)
        .map((c) => ({
          type: "chat" as const,
          id: c.id,
          name: c.name,
        })),
    [chats]
  );

  const actions: ActionItem[] = useMemo(() => {
    if (!onWorkflowPage || !activeBranch) return [];

    const headCommitId = activeBranch.headCommitId;
    if (!headCommitId) return [];

    const { steps, links } = parseCommitData(headCommitFiles);

    const stepsWithIncoming = new Set(links.map((l) => l.toStepId));
    const entrySteps = steps.filter((s) => !stepsWithIncoming.has(s.id));

    if (entrySteps.length === 0) return [];

    return [
      {
        type: "action" as const,
        id: "run-workflow",
        icon: "run" as IconName,
        label: "Run this workflow",
        action: () => {
          for (const step of entrySteps) {
            runStepOptimistically(z, {
              workflowId,
              branchId: activeBranch.id,
              commitId: headCommitId,
              stepDir: step.name,
              ephemeral: false,
            });
          }
        },
      },
    ];
  }, [onWorkflowPage, activeBranch, headCommitFiles, workflowId, z]);

  const filteredItems = useMemo(() => {
    const lowerQuery = query.toLowerCase().trim();
    const codeMatchIds = new Set(workflowsByCodeSearch.map((w) => w.id));

    const filteredQuickLinks = lowerQuery
      ? rankedSearchMatches(quickLinks, (item) => [item.label], lowerQuery)
      : quickLinks;

    const filteredSpaces = lowerQuery
      ? rankedSearchMatches(spaceItems, (item) => [item.name], lowerQuery)
      : spaceItems;

    const filteredWorkflows = lowerQuery
      ? rankedSearchMatches(
          searchableWorkflows,
          (item) => [item.name],
          lowerQuery
        )
      : recentWorkflows.slice(0, 5);

    const codeWorkflows =
      lowerQuery && workflowCodeSearchEnabled
        ? searchableWorkflows.filter(
            (item) =>
              codeMatchIds.has(item.id) &&
              !item.name.toLowerCase().includes(lowerQuery)
          )
        : [];

    const filteredApps = lowerQuery
      ? rankedSearchMatches(
          appItems,
          (item) => [
            item.name,
            connectorsByIdentifier[item.identifier]?.keywords,
          ],
          lowerQuery
        )
      : [];

    const filteredChats = lowerQuery
      ? rankedSearchMatches(chatItems, (item) => [item.name], lowerQuery)
      : chatItems.slice(0, 5);

    const filteredActions = lowerQuery
      ? rankedSearchMatches(actions, (item) => [item.label], lowerQuery)
      : actions;

    return {
      actions: filteredActions,
      quickLinks: filteredQuickLinks,
      spaces: filteredSpaces,
      chats: filteredChats,
      workflows: filteredWorkflows,
      apps: filteredApps,
      codeWorkflows,
    };
  }, [
    query,
    spaceItems,
    recentWorkflows,
    searchableWorkflows,
    workflowsByCodeSearch,
    workflowCodeSearchEnabled,
    appItems,
    chatItems,
    actions,
  ]);

  const allItems: SearchItem[] = useMemo(
    () => [
      ...filteredItems.actions,
      ...filteredItems.quickLinks,
      ...filteredItems.spaces,
      ...filteredItems.chats,
      ...filteredItems.workflows,
      ...filteredItems.apps,
      ...filteredItems.codeWorkflows,
    ],
    [filteredItems]
  );

  // oxlint-disable-next-line react-hooks/exhaustive-deps -- reset index when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Reset state when opening
  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedIndex(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Scroll selected row into view
  useEffect(() => {
    rowRefs.current[selectedIndex]?.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  const handleSelect = useCallback(
    (item: SearchItem) => {
      playClick();
      switch (item.type) {
        case "action":
          item.action();
          break;
        case "quickLink":
          navigate(item.route);
          break;
        case "space":
          navigate(routes.space, { spaceId: item.id });
          break;
        case "chat":
          navigate(routes.chats, { tabId: item.id });
          break;
        case "workflow":
          navigate(routes.space.workflow, {
            spaceId: item.spaceId,
            workflowId: item.id,
          });
          break;
        case "connector":
          navigate(routes.modal.connector, { id: item.id });
          break;
      }
      onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopImmediatePropagation();
        setSelectedIndex((i) => Math.min(i + 1, allItems.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopImmediatePropagation();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopImmediatePropagation();
        const item = allItems[selectedIndex];
        if (item) handleSelect(item);
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [open, selectedIndex, allItems, handleSelect]);

  const selectHint = (
    <Keycap className="select-hint">
      <Icon name="corner-down-left" size={12} />
    </Keycap>
  );

  let rowIndex = 0;
  const renderWorkflowRows = (items: WorkflowItem[]) =>
    items.map((item) => {
      const index = rowIndex++;
      const badgeConfig = parseBadgeConfig(item.badgeSeed, item.id);
      return (
        <Row
          key={item.id}
          ref={(el) => {
            rowRefs.current[index] = el;
          }}
          data-highlighted={index === selectedIndex}
          onMouseEnter={() => setSelectedIndex(index)}
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            handleSelect(item);
          }}
        >
          <div
            style={{
              width: 16,
              height: 16,
              flexShrink: 0,
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <CircleMesh
              key={item.badgeSeed}
              targetCircleSize={8}
              seed={badgeConfig.seed}
              mutingIntensity={0}
              animate={false}
              restrictedColors={badgeConfig.colors}
              grayscale={item.pausedAt != null}
            />
          </div>
          <Text ellipsis variant="text-regular" color="var(--text-primary)">
            {item.name}
            <Text
              variant="text-small"
              color="var(--text-secondary)"
              style={{
                marginLeft: 6,
                verticalAlign: "baseline",
              }}
            >
              {item.spaceName}
            </Text>
          </Text>
          {selectHint}
        </Row>
      );
    });

  return (
    <Modal
      open={open}
      onClose={onClose}
      style={{ marginTop: "20vh", marginBottom: "auto" }}
    >
      <Container>
        <SearchContainer>
          <Icon name="search" size={16} />
          <SearchInput
            ref={inputRef}
            type="text"
            placeholder="Search chats, workflows, spaces…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </SearchContainer>

        <Results>
          {filteredItems.actions.length > 0 && (
            <>
              <SectionHeader>
                <Text variant="text-small">Actions</Text>
              </SectionHeader>
              {filteredItems.actions.map((item) => {
                const index = rowIndex++;
                return (
                  <Row
                    key={item.id}
                    ref={(el) => {
                      rowRefs.current[index] = el;
                    }}
                    data-highlighted={index === selectedIndex}
                    onMouseEnter={() => setSelectedIndex(index)}
                    onMouseDown={(e) => {
                      if (e.button !== 0) return;
                      handleSelect(item);
                    }}
                  >
                    <Icon name={item.icon} size={16} />
                    <Text variant="text-regular" color="var(--text-primary)">
                      {item.label}
                    </Text>
                    {selectHint}
                  </Row>
                );
              })}
            </>
          )}

          {filteredItems.quickLinks.length > 0 && (
            <SectionHeader>
              <Text variant="text-small">Go to</Text>
            </SectionHeader>
          )}
          {filteredItems.quickLinks.length > 0 &&
            filteredItems.quickLinks.map((item) => {
              const index = rowIndex++;
              return (
                <Row
                  key={item.id}
                  ref={(el) => {
                    rowRefs.current[index] = el;
                  }}
                  data-highlighted={index === selectedIndex}
                  onMouseEnter={() => setSelectedIndex(index)}
                  onMouseDown={(e) => {
                    if (e.button !== 0) return;
                    handleSelect(item);
                  }}
                >
                  <Icon name={item.icon} size={16} />
                  <Text variant="text-regular" color="var(--text-primary)">
                    {item.label}
                  </Text>
                  {selectHint}
                </Row>
              );
            })}

          {filteredItems.spaces.length > 0 && (
            <>
              <SectionHeader>
                <Text variant="text-small">Spaces</Text>
              </SectionHeader>
              {filteredItems.spaces.map((item) => {
                const index = rowIndex++;
                return (
                  <Row
                    key={item.id}
                    ref={(el) => {
                      rowRefs.current[index] = el;
                    }}
                    data-highlighted={index === selectedIndex}
                    onMouseEnter={() => setSelectedIndex(index)}
                    onMouseDown={(e) => {
                      if (e.button !== 0) return;
                      handleSelect(item);
                    }}
                  >
                    <Icon name={spaceIcon(item.mode)} size={16} />
                    <Text
                      ellipsis
                      variant="text-regular"
                      color="var(--text-primary)"
                    >
                      {item.name}
                    </Text>
                    {selectHint}
                  </Row>
                );
              })}
            </>
          )}

          {filteredItems.chats.length > 0 && (
            <>
              <SectionHeader>
                <Text variant="text-small">Chats</Text>
              </SectionHeader>
              {filteredItems.chats.map((item) => {
                const index = rowIndex++;
                return (
                  <Row
                    key={item.id}
                    ref={(el) => {
                      rowRefs.current[index] = el;
                    }}
                    data-highlighted={index === selectedIndex}
                    onMouseEnter={() => setSelectedIndex(index)}
                    onMouseDown={(e) => {
                      if (e.button !== 0) return;
                      handleSelect(item);
                    }}
                  >
                    <Icon name="ask" size={16} />
                    <Text
                      ellipsis
                      variant="text-regular"
                      color="var(--text-primary)"
                    >
                      {item.name}
                    </Text>
                    {selectHint}
                  </Row>
                );
              })}
            </>
          )}

          {filteredItems.workflows.length > 0 && (
            <>
              <SectionHeader>
                <Text variant="text-small">Workflows</Text>
              </SectionHeader>
              {renderWorkflowRows(filteredItems.workflows)}
            </>
          )}

          {filteredItems.apps.length > 0 && (
            <>
              <SectionHeader>
                <Text variant="text-small">Connectors</Text>
              </SectionHeader>
              {filteredItems.apps.map((item) => {
                const index = rowIndex++;
                return (
                  <Row
                    key={item.id}
                    ref={(el) => {
                      rowRefs.current[index] = el;
                    }}
                    data-highlighted={index === selectedIndex}
                    onMouseEnter={() => setSelectedIndex(index)}
                    onMouseDown={(e) => {
                      if (e.button !== 0) return;
                      handleSelect(item);
                    }}
                  >
                    <ConnectorLogo app={item} size={16} />
                    <Text
                      ellipsis
                      variant="text-regular"
                      color="var(--text-primary)"
                    >
                      {item.name}
                    </Text>
                    {selectHint}
                  </Row>
                );
              })}
            </>
          )}

          {filteredItems.codeWorkflows.length > 0 && (
            <>
              <SectionHeader>
                <Text variant="text-small">Code matches</Text>
              </SectionHeader>
              {renderWorkflowRows(filteredItems.codeWorkflows)}
            </>
          )}

          {allItems.length === 0 && (
            <EmptyState
              iconName={animalIcon}
              iconSize={24}
              maxWidth={220}
              title="No results"
              description="Try a different search term."
            />
          )}
        </Results>

        <Footer>
          <div style={{ opacity: 0.2 }}>
            <Icon name="tines-mark" size={16} />
          </div>
          <Instructions>
            <Text variant="text-smaller-md">Navigate</Text>
            <div style={{ display: "flex", gap: 2 }}>
              <Keycap>
                <Icon name="arrow-up" size={12} />
              </Keycap>
              <Keycap>
                <Icon name="arrow-down" size={12} />
              </Keycap>
            </div>
            <Icon name="slash" size={12} color="var(--border-alpha)" />
            <Text variant="text-smaller-md">Select</Text>
            <Keycap>
              <Icon name="corner-down-left" size={12} />
            </Keycap>
            <Icon name="slash" size={12} color="var(--border-alpha)" />
            <Text variant="text-smaller-md">Close</Text>
            <Keycap style={{ padding: "2px 4px" }}>
              <Text variant="text-smaller-md">esc</Text>
            </Keycap>
          </Instructions>
        </Footer>
      </Container>
    </Modal>
  );
}
